// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type GestureResponderEvent,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { useI18n } from '@/i18n';
import appColors, { alpha } from '@/theme/colors';
import mapColors from '@/theme/map';

import Svg, { Line, Polygon, Polyline } from 'react-native-svg';

import { ROUGH_FILL } from './holeShapes';
import { pressPointInElement, type MeasurableElement, type PressPointEvent } from './pressPoint';
import {
  applyViewTransform,
  buildTileUrl,
  clampPan,
  computeHoleViewLayout,
  computeLayoutAt,
  framingBoxAround,
  IDENTITY_VIEW,
  MAX_VIEW_ZOOM,
  MIN_VIEW_ZOOM,
  projectToLayout,
  unprojectFromLayout,
  viewportToLayoutPoint,
  type LatLon,
  type ViewTransform,
} from './tileMath';
import { getSatelliteAttribution, getSatelliteTileUrlTemplate } from './tileSource';
import type { TeePoint } from './useHoleViewGeometry';

/** A boundary drawn over the mosaic — a bunker, a pond, a green surface. */
export type MapOverlayPolygon = {
  id: string;
  ring: LatLon[];
  /** Stroke colour; also the fill, unless one is given. */
  color: string;
  /** Fill colour, when it should differ from the stroke — the flat cartographic look. */
  fill?: string;
  /** Defaults to a sheer wash, which is right over imagery and wrong without it. */
  fillOpacity?: number;
};

/**
 * An open line drawn over the mosaic — an out-of-bounds boundary, a stake's heading. Stroked
 * only: it has no inside to fill.
 */
export type MapOverlayLine = {
  id: string;
  path: LatLon[];
  color: string;
  /** SVG dash pattern, e.g. "8,6". Solid when absent. */
  dash?: string;
  strokeWidth?: number;
};

export type HoleMapCanvasProps = {
  tees: TeePoint[];
  green: LatLon | null;
  loading: boolean;
  /** The hole has been looked up and has no mapped green. */
  unavailable: boolean;
  /** Feature boundaries to draw over the imagery. The frame widens to include them. */
  polygons?: MapOverlayPolygon[];
  /** Open lines to draw over the imagery. The frame widens to include them too. */
  polylines?: MapOverlayLine[];
  /**
   * Draws the satellite mosaic. Turning it off leaves the boundaries alone on flat ground,
   * which is how you check a shape against itself rather than against the picture it was cut
   * from — a bunker outline that looks convincing over sand is often a lump of nothing once
   * the sand is taken away. It also costs no tiles, on a phone with no signal to spare.
   */
  showImagery?: boolean;
  /**
   * A single stored picture to draw instead of the XYZ tile grid — the whole-course
   * Lantmateriet mosaic course-analysis-service composited and stored (see that service's
   * mosaic_export.py; never Esri, whose licence forbids retaining the imagery). Independent
   * of `showImagery`/`tileTemplate`: a caller picks one basemap or the other, not both, and
   * this is drawn whenever it is supplied regardless of what `showImagery` says. Positioned
   * from its own geographic corners rather than a z/x/y grid, since it is one image, not tiles.
   */
  storedBasemap?: { url: string; north: number; west: number; south: number; east: number } | null;
  /**
   * Ground to frame when there is no geometry to fit — the club's own coordinates.
   *
   * Without it a hole with nothing marked shows an empty box, which is precisely the hole
   * someone opens the editor to fix.
   */
  fallbackCenter?: LatLon | null;
  /** How much ground the fallback frame covers, corner to corner. */
  fallbackSpanMeters?: number;
  /**
   * Keeps the fallback ground in frame even once there is geometry to fit.
   *
   * For editing. Fitting tightly to what has been placed sounds right and is horrible to
   * use: the first click on an empty hole would slam the view to the minimum span around
   * that one point, leaving the green — a few hundred metres away — off screen.
   */
  holdFallbackFrame?: boolean;
  /**
   * A basemap other than the aerial one — roads rather than imagery.
   *
   * Both must be supplied together or neither: rendering one provider's tiles under another's
   * credit is the one thing every tile licence in this file actually forbids.
   */
  tileUrlTemplate?: string;

  /** @see tileUrlTemplate */
  tileAttribution?: string;

  /** Zoom and pan applied on top of the fitted framing. */
  view?: ViewTransform;
  /** The coordinate under a tap. Supplying this is what makes the canvas interactive. */
  onMapPress?: (point: LatLon) => void;
  /** A drag, in viewport pixels from where it started. Supplying this enables panning. */
  onPan?: (pan: { panX: number; panY: number }) => void;
  /**
   * A tee or green marker being dragged, reported as the ground now under it, move by move.
   *
   * Supplying this is what makes the markers grabbable: a press that starts on one and
   * travels becomes a drag of that marker rather than a pan, and the caller moves the point
   * in its own data as each position arrives — the marker on screen follows because the
   * `tees`/`green` props move, not because the canvas holds any position of its own. A press
   * on a marker that never travels still resolves as a plain `onMapPress` tap, so placement
   * is unaffected. Tees are identified by their index into `tees`.
   */
  onMarkerDrag?: (marker: DraggedMarker, point: LatLon) => void;
  /**
   * The ground under the pointer, as it moves, and null when it leaves the map.
   *
   * Web only — a finger has no position until it touches, so there is nothing to report on a
   * phone and nothing is wired there. For a caller that wants to show what a click *would*
   * do before it happens: the editor draws its measuring line from this. Reported through the
   * same projection `onMapPress` uses, so the previewed point and the placed one cannot drift
   * apart. Silent while the map is being dragged — that pointer is panning, not aiming.
   */
  onHoverPoint?: (point: LatLon | null) => void;
  /**
   * A measuring line drawn over the map, with its length written along it.
   *
   * Distinct from the hole's own chord: dashed and in the accent colour, because it describes
   * something not yet true — where a marker would land, and what that would make the hole
   * measure. The caller owns both ends and the text; this only draws them.
   */
  measureLine?: { from: LatLon; to: LatLon; label: string } | null;
  /**
   * Reads out the coordinate at the centre of the frame, with a hairline cross marking it.
   *
   * For editing. When a hole's geometry is wrong the frame is built around the wrong place,
   * and the imagery alone gives nothing to check that against — one stretch of water looks
   * much like another. A latitude and longitude can be compared with the club's real ones.
   */
  showCenterCoordinate?: boolean;
  /**
   * Reports the same centre coordinate `showCenterCoordinate` draws, as it changes.
   *
   * For callers that need the coordinate as data rather than a label — a location picker
   * confirming whatever the crosshair is over — without duplicating the projection math that
   * produces it.
   */
  onCenterChange?: (point: LatLon | null) => void;
  /** Floor for the view zoom. Editing passes MIN_EDIT_ZOOM to get out past the fitted frame. */
  minZoom?: number;
  /** Ceiling for the view zoom. Editing passes MAX_EDIT_ZOOM to get in close enough to place a
   * marker on a tee box; left alone, this is the player's MAX_VIEW_ZOOM. */
  maxZoom?: number;
  /** Draws an aiming crosshair, so it is clear the next click will place something. */
  aiming?: boolean;
  /**
   * Overrides the frame's own shape. The canvas is square by default, which is right for a
   * phone and wrong for a wide desktop card, where it would push everything else off screen.
   */
  canvasStyle?: StyleProp<ViewStyle>;
  /**
   * The hole's stored playing line, tee end first — drawn in place of the tee-to-green chord,
   * with a ring at every point it bends through.
   *
   * Those bends are the middle of the hole: on a line OSM drew, the shape of the dogleg; on one
   * DerivedCentrelineWriter made, the centre of the fairway at the stations the hole is played
   * through (see FairwayMidpoints — halfway down a par 4, the thirds of a par 5). Either way
   * they are the points the flyover flies through, which is the reason to show them: the chord
   * cuts the corner, and until now the chord was all this map ever drew.
   *
   * Nothing is computed here. The line arrives as it is stored, and a hole that has none keeps
   * the straight chord — which is honest about the fact that nobody has said where it bends.
   */
  centreline?: LatLon[];
  /**
   * Every other hole that already has a tee and a green marked, drawn as a faint line with its
   * number. Paging through eighteen holes one at a time is not the only way to see which are
   * already mapped — the wide picture an unmapped hole falls back to can show that at a glance.
   */
  otherHoles?: KnownHoleMarker[];
  /**
   * The hole being shown, numbered like the rest. Every other hole on the map says which one it
   * is; without this the one actually being looked at was the only anonymous line on it, and
   * telling it apart meant counting round from a neighbour.
   *
   * Drawn black-on-white against the others' white-on-black rather than in the accent colour:
   * the imagery underneath is arbitrary — fairway, bunker sand, water, tree shadow — and a
   * badge has to stay legible on all of it, which is why the muted ones are already a solid
   * disc rather than a tint. Inverting that disc keeps the same guarantee and still reads as
   * "this one is different" at a glance.
   *
   * Not pressable, unlike {@link otherHoles}: it is already the hole on screen, so there is
   * nowhere for a tap to go, and a target sitting on the active line would only swallow taps
   * meant for the map.
   */
  currentHole?: KnownHoleMarker | null;
  /**
   * Tapping a known-hole marker jumps to it. Left out entirely while editing, so a tap near a
   * marker cannot be mistaken for placing or panning.
   */
  onSelectHole?: (holeNumber: number) => void;
  /**
   * What the frame is fit to, when it should differ from what is drawn.
   *
   * `tees`/`green`/`polygons` track an editor's live draft, which changes with every click —
   * fitting the frame to them as well means the view recentres itself the instant a marker
   * goes down, snapping away from the exact spot someone is still looking at. Passing the
   * saved geometry here keeps the frame fixed to what was on screen when editing started,
   * while the drawn points still move freely on top of it. Omitted, the frame fits `tees`/
   * `green`/`polygons` directly, which is right for every read-only caller.
   */
  frame?: { tees: TeePoint[]; green: LatLon | null; polygons?: { ring: LatLon[] }[] };
  /**
   * How much extra ground the fit shows beyond the framed points, as a share of their own
   * span. Defaults to {@link computeHoleViewLayout}'s own defaults (a quarter of the width,
   * a tenth of the length) — generous margins that suit a hole with nothing confirmed yet,
   * where the extra ground is the point. A confirmed hole wants the opposite: tight enough
   * that the hole fills the frame instead of sharing it with whatever is next door.
   */
  horizontalPadFraction?: number;
  /** See {@link horizontalPadFraction}. */
  verticalPadFraction?: number;
  /**
   * Compass bearing the hole plays along, tee to green. Supplied, the whole mosaic turns so
   * the hole runs bottom to top — the same convention the in-round hole view and the
   * schematic guide drawing both already use, which is what lets this map be shown next to
   * either of those and read as the same picture rather than a north-up one. Omitted (the
   * default for every existing caller, editors included), the view stays north-up: an editor
   * placing points wants a stable frame of reference, not one that turns as the geometry
   * being corrected changes.
   */
  holeBearing?: number | null;
  /**
   * Overrides the fit's own minimum span, in metres. Exists for the same reason
   * {@link computeHoleViewLayout}'s own `minSpanMeters` does: fitting to a frame this canvas
   * did not compute itself — one already padded by whoever built it — must not have that
   * floor widen it again on top, or the fit stops matching whatever it was meant to match.
   */
  minSpanMeters?: number;
  /**
   * An explicit camera instead of a fit: hold this coordinate at the centre, cover this
   * much ground, play in this direction. Supplied, it replaces the fitted framing and the
   * `view` transform entirely — the caller owns the camera, frame by frame, which is what
   * the flyover animation does. Everything drawn on top projects through it unchanged.
   */
  camera?: { center: LatLon; spanMeters: number; bearingDegrees: number } | null;
};

/**
 * Which marker a drag has hold of.
 *
 * Tees carry their index into the `tees` prop; a turning point carries its index among the
 * `centreline`'s intermediate points, ends excluded — the same order they are drawn in.
 */
export type DraggedMarker =
  | { kind: 'tee'; index: number }
  | { kind: 'turn'; index: number }
  | { kind: 'green' };

/** A hole with both a tee and a green already known, drawn faintly under the active hole. */
export type KnownHoleMarker = {
  holeNumber: number;
  tee: LatLon;
  green: LatLon;
  /**
   * Where the hole bends, tee end first — the turning points somebody placed on it.
   *
   * Only the placed ones, which is why this needs no par. A turning point nobody has dragged
   * sits on the straight line by construction (see `holeTurnPoints`), so a hole with none
   * draws the same chord it always did, and one with a bend placed at ordinal 1 but not 0
   * draws the same line either way — the missing bend would have landed on the segment being
   * drawn.
   */
  turns?: LatLon[];
};

/** The line a known hole is drawn along: its tee, its bends, its green. */
export const knownHolePath = (hole: KnownHoleMarker): LatLon[] => [
  hole.tee,
  ...(hole.turns ?? []),
  hole.green,
];

/**
 * The point halfway along a polyline, by distance rather than by vertex.
 *
 * Where a hole's number badge goes, so it sits on the line it labels. Halfway between the two
 * ends is the same point on a straight hole and somewhere out in the trees on a dogleg —
 * which is exactly the hole where a number floating loose of its line is hard to read.
 */
export const midpointAlong = <T extends { x: number; y: number }>(points: T[]): { x: number; y: number } => {
  const spans = points.slice(1).map((point, index) => Math.hypot(point.x - points[index].x, point.y - points[index].y));
  const half = spans.reduce((total, span) => total + span, 0) / 2;

  let travelled = 0;
  for (let index = 0; index < spans.length; index += 1) {
    if (travelled + spans[index] >= half) {
      // Zero-length span: the two ends coincide, so any point on it is the midpoint.
      const fraction = spans[index] === 0 ? 0 : (half - travelled) / spans[index];
      return {
        x: points[index].x + (points[index + 1].x - points[index].x) * fraction,
        y: points[index].y + (points[index + 1].y - points[index].y) * fraction,
      };
    }
    travelled += spans[index];
  }
  return points[points.length - 1];
};

/**
 * The hole number geodata gives anything it could not attribute to a hole.
 *
 * It covers two situations that both come down to "we do not know which hole this is": a
 * `golf=hole` way whose ref was missing, out of range or claimed by another way, and a bunker,
 * pond or green that landed on no hole's catchment. Neither is a playable hole, so nothing keyed
 * to this number belongs on a map of the course — the tees and greens under it cannot even be
 * paired with each other, because they are ordinalled by two independent counters.
 *
 * Exported for the callers that have to exclude it; this file never draws it.
 */
export const UNNUMBERED_HOLE = 0;

/**
 * Stands in for a caller that draws no boundaries, so the default is one stable array rather
 * than a fresh `[]` on every render — a new reference here would invalidate the tile layout's
 * memo every render, which is silently wasteful on its own and, for a caller wired to
 * `onCenterChange`, a render loop.
 */
const NO_POLYGONS: MapOverlayPolygon[] = [];

/** Same reasoning as {@link NO_POLYGONS}, for the open lines. */
const NO_POLYLINES: MapOverlayLine[] = [];

/** Same reasoning as {@link NO_POLYGONS}, for the known-hole markers. */
const NO_KNOWN_HOLES: KnownHoleMarker[] = [];

/** Same reasoning as {@link NO_POLYGONS}, for the playing line. */
const NO_CENTRELINE: LatLon[] = [];

/** Below this a press is a tap rather than a drag — a hand is never perfectly still. */
const DRAG_THRESHOLD_PX = 4;

/**
 * How close a press must land to a marker to pick it up rather than the map, in screen
 * pixels. The green's ring is 22 px around; the tee dot is smaller, but grabbing it wants
 * the same reach — a finger is no more precise over a tee than over a green.
 */
const MARKER_GRAB_RADIUS_PX = 22;

const ZOOM_EPSILON = 0.001;

/**
 * The aerial frame of one hole: satellite tiles with the tees, the green and the line
 * between them drawn on top.
 *
 * Shared by the in-round hole view and the admin course-mapping browser, which need the
 * identical picture around very different chrome — one has a distance readout and a close
 * button, the other paginates through holes and lists coordinates. Only the map is common,
 * so only the map lives here.
 *
 * The imagery is a mosaic of raster tiles rather than a map component: the app ships no map
 * SDK, and a static frame is all this needs. Everything drawn on top is positioned by
 * projecting the same coordinates through the layout that placed the tiles.
 */
export function HoleMapCanvas({
  tees,
  green,
  loading,
  unavailable,
  polygons = NO_POLYGONS,
  polylines = NO_POLYLINES,
  showImagery = true,
  tileUrlTemplate,
  tileAttribution,
  storedBasemap = null,
  fallbackCenter = null,
  fallbackSpanMeters = 400,
  holdFallbackFrame = false,
  view = IDENTITY_VIEW,
  onMapPress,
  onPan,
  onMarkerDrag,
  onHoverPoint,
  measureLine = null,
  aiming = false,
  showCenterCoordinate = false,
  onCenterChange,
  minZoom = MIN_VIEW_ZOOM,
  maxZoom = MAX_VIEW_ZOOM,
  canvasStyle,
  centreline = NO_CENTRELINE,
  otherHoles = NO_KNOWN_HOLES,
  currentHole = null,
  onSelectHole,
  frame,
  horizontalPadFraction,
  verticalPadFraction,
  holeBearing = null,
  minSpanMeters,
  camera = null,
}: HoleMapCanvasProps) {
  const { t } = useI18n();
  const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  const onCanvasLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    // Sub-pixel jitter on web would otherwise recompute the whole mosaic on every frame.
    const next = { width: Math.round(width), height: Math.round(height) };
    setSize((current) =>
      current.width === next.width && current.height === next.height ? current : next
    );
  };

  const fitted = useMemo(() => {
    // An explicit camera replaces the fit outright: the caller says where to look.
    if (camera) {
      return computeLayoutAt({
        center: camera.center,
        spanMeters: camera.spanMeters,
        bearingDegrees: camera.bearingDegrees,
        width: size.width,
        height: size.height,
      });
    }
    // What the frame fits to, as opposed to what gets drawn: see the `frame` prop doc. Most
    // callers pass no override, so this is just `tees`/`green`/`polygons` again.
    const frameTees = frame ? frame.tees : tees;
    const frameGreen = frame ? frame.green : green;
    const ringPoints = [
      ...(frame ? (frame.polygons ?? []) : polygons).flatMap((polygon) => polygon.ring),
      ...(frame ? [] : polylines).flatMap((line) => line.path),
    ];
    const nothingToFrame = frameTees.length === 0 && frameGreen === null && ringPoints.length === 0;
    // Framed on the surrounding ground when asked to hold it, and always when there is
    // nothing else to frame — an unmapped hole would otherwise be an empty box, which is
    // exactly the hole somebody opened this to fix.
    const groundPoints =
      fallbackCenter && (holdFallbackFrame || nothingToFrame)
        ? framingBoxAround(fallbackCenter, fallbackSpanMeters)
        : [];

    return computeHoleViewLayout({
      tees: frameTees,
      green: frameGreen,
      width: size.width,
      height: size.height,
      // A greenside bunker sits outside the tee-to-green box; framing on every ring
      // vertex keeps whole boundaries in view instead of clipping them at the edge.
      extraPoints: [...ringPoints, ...groundPoints],
      horizontalPadFraction,
      verticalPadFraction,
      holeBearing,
      minSpanMeters,
    });
  }, [
    camera,
    fallbackCenter,
    fallbackSpanMeters,
    frame,
    green,
    holdFallbackFrame,
    holeBearing,
    horizontalPadFraction,
    minSpanMeters,
    polygons,
    polylines,
    size.height,
    size.width,
    tees,
    verticalPadFraction,
  ]);

  const layout = useMemo(
    () =>
      fitted
        ? // A caller-owned camera is already exactly where it wants to be; zooming or panning
          // it from a stale `view` would fight the animation driving it.
          camera
          ? fitted
          : applyViewTransform(fitted, view, undefined, {
              minZoom,
              maxViewZoom: maxZoom,
              // Offering a pan handler is what marks this as an editing surface, and editing is
              // the case that needs to leave the fitted frame rather than roam inside it.
              freePan: onPan !== undefined,
            })
        : null,
    [camera, fitted, maxZoom, minZoom, onPan, view]
  );

  // Defaulted, not required: every existing caller wants the aerial imagery and says nothing,
  // and the club locator wants roads because at the span it opens on, imagery is an
  // undifferentiated green blur while a road map is legible.
  const tileTemplate = useMemo(
    () => tileUrlTemplate ?? getSatelliteTileUrlTemplate(),
    [tileUrlTemplate]
  );
  const attribution = useMemo(
    () => tileAttribution ?? getSatelliteAttribution(),
    [tileAttribution]
  );

  /**
   * The coordinate under the middle of the frame, recomputed as the view moves.
   *
   * Read back out through the same projection that placed the tiles, so it is the imagery's
   * own answer rather than a second calculation that could disagree with what is on screen.
   */
  const centerCoordinate = useMemo(() => {
    if (!showCenterCoordinate || !layout) return null;
    return unprojectFromLayout(
      viewportToLayoutPoint({ x: size.width / 2, y: size.height / 2 }, layout),
      layout
    );
  }, [layout, showCenterCoordinate, size.height, size.width]);

  useEffect(() => {
    onCenterChange?.(centerCoordinate);
  }, [centerCoordinate, onCenterChange]);

  const teePoints = useMemo(
    () => (layout ? tees.map((tee) => projectToLayout(tee, layout)) : []),
    [layout, tees]
  );
  const greenPoint = useMemo(
    () => (layout && green ? projectToLayout(green, layout) : null),
    [green, layout]
  );
  // Two points is a straight line the chord already draws; the bends are the whole content, so
  // a line with none of them is left to the chord rather than drawn twice.
  const centrelinePoints = useMemo(
    () =>
      layout && centreline.length > 2
        ? centreline.map((point) => projectToLayout(point, layout))
        : [],
    [centreline, layout]
  );

  const interactive = onMapPress !== undefined || onPan !== undefined || onMarkerDrag !== undefined;

  /**
   * The gesture in progress, or null.
   *
   * A ref rather than state: it changes on every move event and nothing on screen reads it,
   * so re-rendering the mosaic for each one would only cost frames.
   */
  const gestureRef = useRef<{
    mode: 'pending' | 'pan' | 'marker';
    startX: number;
    startY: number;
    startView: ViewTransform;
    /** The marker the press went down on, if any — what a travelled press picks up. */
    marker: DraggedMarker | null;
  } | null>(null);

  /**
   * The marker under a viewport point, or null. Compared in layout coordinates, the same
   * space the markers are drawn in, so the grab radius is the marker as it appears on
   * screen. Nearest wins when a tee and the green overlap.
   */
  const markerAt = (point: { x: number; y: number }): DraggedMarker | null => {
    if (!onMarkerDrag || !layout) return null;
    const layoutPoint = viewportToLayoutPoint(point, layout);
    const candidates: { marker: DraggedMarker; at: { x: number; y: number } }[] = [
      ...teePoints.map((tee, index) => ({ marker: { kind: 'tee', index } as const, at: tee })),
      // The line's bends. Grabbable only because `onMarkerDrag` is supplied, which the editor
      // does exactly while it is also feeding `centreline` its own draft — a read-only caller
      // draws a stored line and hands no drag handler, so its vertices stay untouchable.
      ...centrelinePoints
        .slice(1, -1)
        .map((point, index) => ({ marker: { kind: 'turn', index } as const, at: point })),
      ...(greenPoint ? [{ marker: { kind: 'green' } as const, at: greenPoint }] : []),
    ];
    let nearest: { marker: DraggedMarker; distance: number } | null = null;
    for (const candidate of candidates) {
      const distance = Math.hypot(candidate.at.x - layoutPoint.x, candidate.at.y - layoutPoint.y);
      if (distance <= MARKER_GRAB_RADIUS_PX && (!nearest || distance < nearest.distance)) {
        nearest = { marker: candidate.marker, distance };
      }
    }
    return nearest?.marker ?? null;
  };

  // `currentTarget` is the canvas element on web, where the click event carries no
  // element-relative coordinates of its own; on native the touch coordinates are used.
  const canvasPointOf = (event: GestureResponderEvent) =>
    pressPointInElement(
      event.nativeEvent as PressPointEvent,
      event.currentTarget as unknown as MeasurableElement
    );

  const onGestureStart = (event: GestureResponderEvent) => {
    const point = canvasPointOf(event);
    if (!point) return;
    // Undecided until the pointer either travels (a pan, or a marker drag when it went down
    // on one) or lifts (a placement). Deciding on the way down would drop a marker every
    // time someone went to drag the map.
    gestureRef.current = {
      mode: 'pending',
      startX: point.x,
      startY: point.y,
      startView: view,
      marker: markerAt(point),
    };
  };

  const onGestureMove = (event: GestureResponderEvent) => {
    const gesture = gestureRef.current;
    const point = canvasPointOf(event);
    if (!gesture || !point) return;

    if (gesture.mode === 'pending') {
      const travelled = Math.hypot(point.x - gesture.startX, point.y - gesture.startY);
      if (travelled < DRAG_THRESHOLD_PX) return;
      // A press that went down on a marker takes the marker with it; anywhere else it takes
      // the map. Decided here rather than on the way down so a plain tap on a marker still
      // resolves as a placement.
      if (gesture.marker && onMarkerDrag) {
        gesture.mode = 'marker';
      } else {
        // Panning is allowed at every zoom, including the fit. It used to be refused at the
        // fit on the reasoning that a frame built around the hole has nowhere to pan to — which
        // holds right up until the hole's geometry is wrong or missing, and the frame fits
        // neatly around a lake two kilometres away. That is exactly when someone needs to drag
        // the map, and it was the one moment they could not. Placement is unaffected: a press
        // that travels less than the drag threshold still resolves as a tap.
        if (!onPan) return;
        gesture.mode = 'pan';
      }
    }

    if (gesture.mode === 'marker') {
      if (!gesture.marker || !layout) return;
      // The ground now under the pointer, through the same projection a click uses, so the
      // dragged marker lands exactly where it was let go.
      onMarkerDrag?.(
        gesture.marker,
        unprojectFromLayout(viewportToLayoutPoint(point, layout), layout)
      );
      return;
    }

    const panned = {
      panX: gesture.startView.panX + (point.x - gesture.startX),
      panY: gesture.startView.panY + (point.y - gesture.startY),
    };
    // Reported already cut down to what the map can actually be moved by. Handing back the
    // raw figure let a caller keep accumulating drags past the limit; the view then sat on
    // the clamp while the stored pan ran away from it, and every further drag — in either
    // direction — stayed inside the clamp too, so the map looked frozen.
    onPan?.(
      fitted
        ? clampPan(fitted, { ...view, ...panned }, { minZoom, maxViewZoom: maxZoom, freePan: true })
        : panned
    );
  };

  /**
   * A press that never travelled places the point, at the position it went down.
   *
   * Down rather than up so the marker lands where the pointer was aimed, not wherever it
   * drifted to on the way back off the screen.
   */
  const onGestureEnd = () => {
    const gesture = gestureRef.current;
    gestureRef.current = null;
    if (!gesture || gesture.mode !== 'pending' || !layout || !onMapPress) return;
    onMapPress(
      unprojectFromLayout(
        viewportToLayoutPoint({ x: gesture.startX, y: gesture.startY }, layout),
        layout
      )
    );
  };

  /**
   * Mouse tracking, on web only.
   *
   * react-native-web forwards `onMouseMove`/`onMouseLeave` straight to the DOM node, and the
   * event it hands back is a plain MouseEvent — which is exactly what `pressPointInElement`
   * already knows how to read (it does the same for a web click). Cast into the View's props
   * because React Native's own types have no mouse events to declare.
   */
  const hoverProps =
    Platform.OS === 'web' && onHoverPoint
      ? ({
          onMouseMove: (event: {
            nativeEvent?: PressPointEvent;
            currentTarget?: unknown;
          }) => {
            // A pointer held down and travelling is dragging the map or a marker, not aiming.
            if (gestureRef.current?.mode === 'pan' || gestureRef.current?.mode === 'marker') return;
            const point = pressPointInElement(
              event.nativeEvent,
              event.currentTarget as MeasurableElement
            );
            if (!point || !layout) {
              onHoverPoint(null);
              return;
            }
            onHoverPoint(unprojectFromLayout(viewportToLayoutPoint(point, layout), layout));
          },
          onMouseLeave: () => onHoverPoint(null),
        } as object)
      : null;

  const measurePoints = useMemo(
    () =>
      layout && measureLine
        ? {
            from: projectToLayout(measureLine.from, layout),
            to: projectToLayout(measureLine.to, layout),
          }
        : null,
    [layout, measureLine]
  );

  const responderProps = interactive
    ? {
        onStartShouldSetResponder: () => true,
        onMoveShouldSetResponder: () => true,
        onResponderGrant: onGestureStart,
        onResponderMove: onGestureMove,
        onResponderRelease: onGestureEnd,
        onResponderTerminate: onGestureEnd,
        onResponderTerminationRequest: () => false,
      }
    : null;

  return (
    <View
      style={[
        styles.canvas,
        canvasStyle,
        // Everything not otherwise mapped is rough, the same ground the yardage-book drawing
        // in the hole view starts from.
        showImagery ? null : styles.canvasNoImagery,
        aiming ? styles.canvasAiming : null,
      ]}
      onLayout={onCanvasLayout}
      testID="hole-view-canvas"
      {...responderProps}
      {...hoverProps}
    >
      {layout ? (
        <>
          {/* The mosaic and everything pinned to the ground it shows — turned as one block
              when `holeBearing` is given, same as the in-round hole view and the guide
              drawing both already turn. A no-op wrapper when it is not: at zero rotation
              `width`/`height` equal the viewport and this box sits at (0, 0), exactly where
              its children used to be rendered directly. */}
          <View
            testID="hole-view-rotator"
            pointerEvents="box-none"
            style={{
              position: 'absolute',
              left: (layout.viewportWidth - layout.width) / 2,
              top: (layout.viewportHeight - layout.height) / 2,
              width: layout.width,
              height: layout.height,
              transform: [{ rotate: `${layout.rotationDegrees}deg` }],
            }}
          >
            {/* Skipped rather than covered: the point of the imagery-free view is that it
                fetches nothing. */}
            {showImagery
              ? layout.tiles.map((tile) => (
                  <Image
                    key={`${tile.z}/${tile.x}/${tile.y}`}
                    testID={`hole-view-tile-${tile.z}-${tile.x}-${tile.y}`}
                    source={{ uri: buildTileUrl(tileTemplate, tile) }}
                    style={[
                      styles.tile,
                      {
                        left: tile.left,
                        top: tile.top,
                        width: layout.tileSize,
                        height: layout.tileSize,
                      },
                    ]}
                  />
                ))
              : null}

            {/* One image, not a grid: positioned from its own corners rather than a z/x/y
                tile set, since that is all a single stored picture has to place itself by. */}
            {storedBasemap
              ? (() => {
                  const topLeft = projectToLayout(
                    { lat: storedBasemap.north, lon: storedBasemap.west },
                    layout
                  );
                  const bottomRight = projectToLayout(
                    { lat: storedBasemap.south, lon: storedBasemap.east },
                    layout
                  );
                  return (
                    <Image
                      testID="hole-view-stored-basemap"
                      source={{ uri: storedBasemap.url }}
                      style={{
                        position: 'absolute',
                        left: topLeft.x,
                        top: topLeft.y,
                        width: bottomRight.x - topLeft.x,
                        height: bottomRight.y - topLeft.y,
                      }}
                    />
                  );
                })()
              : null}

            {polygons.length > 0 ? (
              <Svg
                testID="hole-view-polygons"
                pointerEvents="none"
                style={StyleSheet.absoluteFill}
                width={layout.width}
                height={layout.height}
              >
                {polygons.map((overlay) => {
                  const points = overlay.ring
                    .map((vertex) => projectToLayout(vertex, layout))
                    .map((point) => `${point.x},${point.y}`)
                    .join(' ');
                  return (
                    <Polygon
                      key={overlay.id}
                      points={points}
                      stroke={overlay.color}
                      strokeWidth={2}
                      strokeLinejoin="round"
                      fill={overlay.fill ?? overlay.color}
                      fillOpacity={overlay.fillOpacity ?? 0.3}
                    />
                  );
                })}
              </Svg>
            ) : null}

            {polylines.length > 0 ? (
              <Svg
                testID="hole-view-polylines"
                pointerEvents="none"
                style={StyleSheet.absoluteFill}
                width={layout.width}
                height={layout.height}
              >
                {polylines.map((overlay) => {
                  const points = overlay.path
                    .map((vertex) => projectToLayout(vertex, layout))
                    .map((point) => `${point.x},${point.y}`)
                    .join(' ');
                  return (
                    <Polyline
                      key={overlay.id}
                      points={points}
                      stroke={overlay.color}
                      strokeWidth={overlay.strokeWidth ?? 3}
                      strokeDasharray={overlay.dash}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      fill="none"
                    />
                  );
                })}
              </Svg>
            ) : null}

            {/* The stored line wins over the chord where there is one: they describe the same
                hole, and drawing both would read as two holes crossing. */}
            {centrelinePoints.length > 0 ? (
              centrelinePoints.slice(1).map((point, index) => (
                <HoleLine
                  key={`centreline-${index}`}
                  testID={`hole-view-centreline-segment-${index}`}
                  from={centrelinePoints[index]}
                  to={point}
                />
              ))
            ) : greenPoint && teePoints.length > 0 ? (
              <HoleLine from={teePoints[0]} to={greenPoint} />
            ) : null}

            {/* Ends excluded: those are the tee and the green, which have markers of their own.
                What is left is where the hole bends — the middle of the fairway. */}
            {centrelinePoints.slice(1, -1).map((point, index) => (
              <View
                key={`fairway-point-${index}`}
                testID={`hole-view-fairway-point-${index}`}
                style={[styles.fairwayMarker, { left: point.x - 11, top: point.y - 11 }]}
                pointerEvents="none"
              >
                <View style={styles.fairwayMarkerRing} />
                <View style={styles.fairwayMarkerDot} />
              </View>
            ))}

            {teePoints.map((point, index) => (
              <View
                key={`tee-${index}`}
                testID={`hole-view-tee-${index}`}
                style={[styles.teeMarkerGroup, { left: point.x - 7, top: point.y - 7 }]}
                pointerEvents="none"
              >
                <View style={styles.teeMarker} />
                {tees[index]?.label ? (
                  <Text
                    style={[styles.teeLabel, { transform: [{ rotate: `${-layout.rotationDegrees}deg` }] }]}
                    numberOfLines={1}
                  >
                    {tees[index].label}
                  </Text>
                ) : null}
              </View>
            ))}

            {greenPoint ? (
              <View
                testID="hole-view-green-marker"
                style={[styles.greenMarker, { left: greenPoint.x - 22, top: greenPoint.y - 22 }]}
                pointerEvents="none"
              >
                <View style={styles.greenMarkerRing} />
                <View style={styles.greenMarkerDot} />
              </View>
            ) : null}

            {otherHoles.flatMap((hole) => {
              // Its own bends, not a chord through them: a neighbouring dogleg drawn straight
              // runs through the trees between its two ends, which reads as a hole that is not
              // there and hides the one that is.
              const path = knownHolePath(hole).map((point) => projectToLayout(point, layout));
              const badge = midpointAlong(path);
              const badgeStyle = [
                styles.knownHoleBadge,
                { left: badge.x - 11, top: badge.y - 11 },
              ];
              return [
                ...path.slice(1).map((point, index) => (
                  <HoleLine
                    key={`known-hole-line-${hole.holeNumber}-${index}`}
                    from={path[index]}
                    to={point}
                    muted
                  />
                )),
                onSelectHole ? (
                  <Pressable
                    key={`known-hole-badge-${hole.holeNumber}`}
                    // The canvas claims the responder on any move; an Android tap always
                    // moves a little, so without this the badge's press went to the pan.
                    cancelable={false}
                    testID={`hole-view-known-hole-${hole.holeNumber}`}
                    accessibilityRole="button"
                    onPress={() => onSelectHole(hole.holeNumber)}
                    style={badgeStyle}
                  >
                    <Text style={styles.knownHoleBadgeText}>{hole.holeNumber}</Text>
                  </Pressable>
                ) : (
                  <View
                    key={`known-hole-badge-${hole.holeNumber}`}
                    testID={`hole-view-known-hole-${hole.holeNumber}`}
                    pointerEvents="none"
                    style={badgeStyle}
                  >
                    <Text style={styles.knownHoleBadgeText}>{hole.holeNumber}</Text>
                  </View>
                ),
              ];
            })}

            {/* The measuring line, over the geometry it is being compared against: it answers
                "what would this click make the hole", so it has to be readable across whatever
                the pointer is currently over. */}
            {measurePoints ? (
              <>
                <Svg
                  testID="hole-view-measure-line"
                  pointerEvents="none"
                  style={StyleSheet.absoluteFill}
                  width={layout.width}
                  height={layout.height}
                >
                  <Line
                    x1={measurePoints.from.x}
                    y1={measurePoints.from.y}
                    x2={measurePoints.to.x}
                    y2={measurePoints.to.y}
                    stroke={appColors.accent.primary}
                    strokeWidth={2}
                    strokeDasharray="7 5"
                  />
                </Svg>
                <View
                  testID="hole-view-measure-label"
                  pointerEvents="none"
                  style={[
                    styles.measureLabel,
                    {
                      left: (measurePoints.from.x + measurePoints.to.x) / 2,
                      top: (measurePoints.from.y + measurePoints.to.y) / 2,
                      transform: [{ rotate: `${-layout.rotationDegrees}deg` }],
                    },
                  ]}
                >
                  <Text style={styles.measureLabelText} numberOfLines={1}>
                    {measureLine?.label}
                  </Text>
                </View>
              </>
            ) : null}

            {/* Last, so it sits over any muted badge it happens to overlap: two holes whose
                chords cross put their midpoints in nearly the same place, and the one being
                looked at is the one that has to stay readable. */}
            {currentHole ? (
              (() => {
                // Unnumbered holes carry a green with no tee paired to it, so there is no
                // chord to hang the badge halfway along. The pager only ever holds numbered
                // holes, so this is the type being honest rather than a case that arises.
                const tee = currentHole.tee;
                if (!tee) return null;
                const badge = midpointAlong(
                  knownHolePath(currentHole).map((point) => projectToLayout(point, layout))
                );
                return (
                  <View
                    testID={`hole-view-current-hole-${currentHole.holeNumber}`}
                    pointerEvents="none"
                    style={[
                      styles.currentHoleBadge,
                      { left: badge.x - 11, top: badge.y - 11 },
                    ]}
                  >
                    <Text style={styles.currentHoleBadgeText}>{currentHole.holeNumber}</Text>
                  </View>
                );
              })()
            ) : null}
          </View>

          {/* Marks what the readout below is measuring. Without it the number is the centre
              of something the eye has to guess at. */}
          {centerCoordinate ? (
            <View style={styles.centerCross} pointerEvents="none" testID="hole-map-center-cross">
              <View style={styles.centerCrossVertical} />
              <View style={styles.centerCrossHorizontal} />
            </View>
          ) : null}

          {/* The imagery credit belongs to the imagery; there is none to credit without it. */}
          {showImagery ? <Text style={styles.attribution}>{attribution}</Text> : null}

          {/* Five decimals is about a metre — finer than the imagery resolves, and enough to
              compare against a club's published position. */}
          {centerCoordinate ? (
            <Text style={styles.centerCoordinate} testID="hole-map-center-coordinate">
              {centerCoordinate.lat.toFixed(5)}, {centerCoordinate.lon.toFixed(5)}
            </Text>
          ) : null}
        </>
      ) : null}

      {loading ? (
        <View style={styles.overlay} testID="hole-view-loading">
          <ActivityIndicator color={appColors.accent.primary} />
        </View>
      ) : unavailable ? (
        <View style={styles.overlay} testID="hole-view-unavailable">
          <Text style={styles.overlayText}>{t('hole.holeViewUnavailable')}</Text>
        </View>
      ) : null}
    </View>
  );
}

/** True once there is a green to frame, which is what the legend describes. */
export const hasMappedGreen = (green: LatLon | null): boolean => green !== null;

/** The tee-to-green line, drawn as a rotated bar so no drawing library is needed. */
function HoleLine({
  from,
  to,
  muted = false,
  testID,
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  /** The faint version drawn for every other mapped hole, so the active hole's line still stands out. */
  muted?: boolean;
  /** Overrides the default id, for a line drawn as one of several segments. */
  testID?: string;
}) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (!(length > 0)) return null;
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  return (
    <View
      testID={testID ?? (muted ? 'hole-view-known-hole-line' : 'hole-view-line')}
      pointerEvents="none"
      style={[
        styles.holeLine,
        muted ? styles.holeLineMuted : null,
        {
          left: (from.x + to.x) / 2 - length / 2,
          top: (from.y + to.y) / 2,
          width: length,
          transform: [{ rotate: `${angle}deg` }],
        },
      ]}
    />
  );
}

const styles = StyleSheet.create({
  canvas: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: appColors.background.panel,
  },
  canvasNoImagery: { backgroundColor: ROUGH_FILL },
  // The pointer is the whole instruction on a laptop: a crosshair says "click the ground",
  // where the default arrow says nothing at all.
  canvasAiming: Platform.select({ web: { cursor: 'crosshair' }, default: {} }) as object,
  tile: { position: 'absolute' },
  holeLine: {
    position: 'absolute',
    height: 2,
    backgroundColor: alpha(appColors.text.onMedia, 0.75),
  },
  // Anchored at the line's midpoint and offset up and left by roughly half its own size, so it
  // straddles the line rather than hanging off one end of it.
  measureLabel: {
    position: 'absolute',
    marginLeft: -34,
    marginTop: -11,
    minWidth: 68,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: alpha(appColors.background.app, 0.82),
    alignItems: 'center',
  },
  measureLabelText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  holeLineMuted: {
    height: 1.5,
    backgroundColor: alpha(appColors.text.onMedia, 0.35),
  },
  knownHoleBadge: {
    position: 'absolute',
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(appColors.background.panel, 0.85),
    borderWidth: 1,
    borderColor: alpha(appColors.text.onMedia, 0.6),
  },
  knownHoleBadgeText: { color: appColors.text.onMedia, fontSize: 10, fontWeight: '700' },
  // The muted badge inverted: same disc, same ring width, opaque rather than 85% so the
  // imagery never shows through the one badge that has to be unmistakable.
  currentHoleBadge: {
    position: 'absolute',
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: appColors.text.onMedia,
    borderWidth: 1,
    borderColor: alpha(appColors.background.panel, 0.6),
  },
  currentHoleBadgeText: { color: appColors.background.panel, fontSize: 10, fontWeight: '700' },
  teeMarkerGroup: { position: 'absolute', alignItems: 'center' },
  teeMarker: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: appColors.accent.gold,
    borderWidth: 2,
    borderColor: alpha(appColors.neutral.black, 0.55),
  },
  teeLabel: {
    color: appColors.text.onMedia,
    fontSize: 10,
    fontWeight: '700',
    marginTop: 2,
    textShadowColor: alpha(appColors.neutral.black, 0.9),
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
    maxWidth: 90,
  },
  greenMarker: { position: 'absolute', width: 44, height: 44, alignItems: 'center', justifyContent: 'center' },
  greenMarkerRing: {
    position: 'absolute',
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 2,
    borderColor: alpha(mapColors.marker, 0.9),
  },
  greenMarkerDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: mapColors.marker },
  // Smaller and white, to read as a point on the line rather than a third thing next to the
  // tee and the green markers.
  fairwayMarker: { position: 'absolute', width: 22, height: 22, alignItems: 'center', justifyContent: 'center' },
  fairwayMarkerRing: {
    position: 'absolute',
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: alpha(appColors.text.onMedia, 0.9),
  },
  fairwayMarkerDot: { width: 5, height: 5, borderRadius: 2.5, backgroundColor: alpha(appColors.text.onMedia, 0.9) },
  attribution: {
    position: 'absolute',
    right: 4,
    bottom: 2,
    color: alpha(appColors.text.onMedia, 0.75),
    fontSize: 9,
    textShadowColor: alpha(appColors.neutral.black, 0.9),
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  // Opposite corner to the attribution, which owns bottom-right.
  centerCoordinate: {
    position: 'absolute',
    left: 6,
    bottom: 4,
    color: appColors.text.onMedia,
    fontSize: 11,
    fontWeight: '700',
    // Digits that change on every frame of a drag must not shuffle their own width.
    fontVariant: ['tabular-nums'],
    textShadowColor: alpha(appColors.neutral.black, 0.9),
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  centerCross: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    width: 22,
    height: 22,
    marginLeft: -11,
    marginTop: -11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  centerCrossVertical: {
    position: 'absolute',
    width: 1,
    height: 22,
    backgroundColor: alpha(appColors.text.onMedia, 0.85),
  },
  centerCrossHorizontal: {
    position: 'absolute',
    width: 22,
    height: 1,
    backgroundColor: alpha(appColors.text.onMedia, 0.85),
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(appColors.background.app, 0.72),
    padding: 16,
  },
  overlayText: { color: appColors.text.primary, fontSize: 14, textAlign: 'center' },
});
